import type { ReactElement } from 'react';

import { getFragmentData } from '@e-socna/data-layer';
import { ensureDefined, invariant } from '@e-socna/utils';
import { RESOLVER_CONTENT_CLASSNAME } from '@e-socna/printer-contract';

import { useMissionContextOptional } from '../../../contexts/MissionContext/hooks/useMissionContextOptional.hook';
import { MissionContextStatus } from '../../../contexts/MissionContext/types/mission-context.type';
import { MissionQuotationPage_QuotationInfo_Fragment } from '../../../pages/MissionQuotation/fragments/quotation-info.fragment';
import { formatDate } from '../../../utils/format-date.util';

import { HtmlViewer } from '../../HtmlViewer';

import type {
  ResolverParameters,
  ResolverResult,
} from '../types/resolvers.type';

export const useDevisNota = ({
  enabled,
}: Readonly<ResolverParameters>): ResolverResult<ReactElement> => {
  const missionContext = useMissionContextOptional();

  if (!enabled) {
    return {
      data: undefined,
      error: null,
      isSuccess: false,
      isLoading: false,
    };
  }

  invariant(!!missionContext, 'Mission context is required');

  const { status, mission } = missionContext;

  switch (status) {
    case MissionContextStatus.Error:
      return {
        data: undefined,
        error: new Error('Error loading mission context'),
        isSuccess: false,
        isLoading: false,
      };
    case MissionContextStatus.Loading:
      return {
        data: undefined,
        error: null,
        isSuccess: false,
        isLoading: true,
      };
    case MissionContextStatus.NotInitialized:
      return {
        data: undefined,
        error: new Error('Mission context not initialized'),
        isSuccess: false,
        isLoading: false,
      };
    case MissionContextStatus.Ready: {
      const {
        quotationInfo: {
          header: { note, interventionDate },
        },
      } = ensureDefined({
        quotationInfo: getFragmentData(
          MissionQuotationPage_QuotationInfo_Fragment,
          mission.quotationInfo,
        ),
      });

      const generated = (
        <div className={RESOLVER_CONTENT_CLASSNAME}>
          <HtmlViewer
            html={
              // eslint-disable-next-line @typescript-eslint/prefer-nullish-coalescing
              note || "Aucun nota n'a été spécifié."
            }
          />
          {!!interventionDate && (
            <p>
              <strong>Intervention le {formatDate(interventionDate)}</strong>
            </p>
          )}
        </div>
      );

      return {
        data: { generated },
        error: null,
        isSuccess: true,
        isLoading: false,
      };
    }
    default: {
      throw new Error('Unhandled mission context status');
    }
  }
};
